import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { compact as _compact, map as _map } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter as rxFilter, map, startWith, tap } from 'rxjs/operators';
import { IACOption } from 'projects/shared/nvps-libraries/design/nv-textbox/nv-textbox.interface';
import { NvTextboxVirtualScrollComponent } from 'Src/nvps-libraries/design/nv-textbox-virtual-scroll/nv-textbox-virtual-scroll.component';
import { NvTextboxVirtualScrollModule } from 'Src/nvps-libraries/design/nv-textbox-virtual-scroll/nv-textbox-virtual-scroll.module';
import { ISearchPerformed } from '../global-search-adapter/global-search-adapter.component';

/** How long typing must pause before it counts as one settled search. Matches `student-search`. */
const SEARCH_SETTLE_MS = 500;

export interface IEntitySearchOption {
  entityId: string;
  entityName: string;
  entityNickName?: string;
}

/**
 * entity-search — autocomplete for jumping to a school/shelter entity profile.
 *
 * Mirrors the structure of `student-search` but formats entity names directly
 * (entity names have no "Last, First" convention, so the student name formatter
 * cannot be reused).
 */
@Component({
  selector: 'entity-search',
  templateUrl: './entity-search.component.html',
  styleUrls: ['./entity-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NvTextboxVirtualScrollModule],
})
export class EntitySearchComponent implements OnInit, OnDestroy {
  @ViewChild(NvTextboxVirtualScrollComponent) virtualScroll: NvTextboxVirtualScrollComponent;
  @Input() entities: IEntitySearchOption[] = [];
  @Input() placeholder: string;
  @Input() isDisabled: boolean = false;
  @Input() isInverted: boolean = false;
  @Input() roundedCorner: boolean = false;
  @Output() selectedEntityId: EventEmitter<string> = new EventEmitter();
  @Output() searchPerformed: EventEmitter<ISearchPerformed> = new EventEmitter();

  public entitySearchControl: FormControl = new FormControl();
  public formattedEntities: IACOption[] = [];
  public filteredEntities$: Observable<IACOption[]>;
  public menuHeight: string = '';
  public customStylingWrapperId: string = 'virtual-scroll-entity-search';
  public emptyStateText: string = 'No entity found';
  public autocompleteHasActiveState = true;
  public icon: string = 'search-large-black';

  private latestFilteredEntities: IACOption[] = [];
  private settledSearchSub: Subscription;

  public ngOnInit (): void {
    this.icon = this.isInverted ? 'search-large-white' : 'search-large-black';
    this.placeholder = this.placeholder ?? 'Search...';
    this.formattedEntities = this.entities.map(entity => ({
      key: entity.entityId,
      human: entity.entityName,
      tags: [{
        key: entity.entityId,
        human: entity.entityNickName ? `${entity.entityId} | ${entity.entityNickName}` : entity.entityId,
      }],
    }));
    this.filteredEntities$ = this.entitySearchControl.valueChanges.pipe(
      startWith(''),
      map(val => this.filter(val)),
      tap(filteredEntities => { this.latestFilteredEntities = filteredEntities; }),
    );
    this.menuHeight = this.calculateMenuHeight(this.entities.length);

    this.watchSettledSearches();
  }

  public ngOnDestroy (): void {
    this.settledSearchSub?.unsubscribe();
  }

  /**
   * Emits once per settled search rather than once per keystroke, so a report counts searches instead
   * of typing.
   */
  private watchSettledSearches (): void {
    this.settledSearchSub?.unsubscribe();

    this.settledSearchSub = this.entitySearchControl.valueChanges.pipe(
      // Selecting an option pushes an IACOption through this same control; only typed text is a search.
      rxFilter((val): val is string => typeof val === 'string' && !!val.trim()),
      debounceTime(SEARCH_SETTLE_MS),
      distinctUntilChanged(),
    ).subscribe((val: string) => {
      this.searchPerformed.emit({
        searchTextLength: val.trim().length,
        searchResultCount: this.latestFilteredEntities.length,
      });
    });
  }

  public calculateMenuHeight (count: number): string {
    switch (count) {
      case 1:
        return 'one-item-menu';
      case 2:
        return 'two-item-menu';
      case 3:
        return 'three-item-menu';
      case 4:
        return 'four-item-menu';
      default:
        return 'full-menu';
    }
  }

  private filter (val: string | IACOption): IACOption[] {
    // An option object is emitted when a selection is made — don't filter.
    if (val && typeof val === 'object') {
      this.menuHeight = this.calculateMenuHeight(this.entities.length);
      return this.formattedEntities;
    }

    if (val && typeof val === 'string') {
      let searchTerms = _compact(val.replace(',', ' ').split(' '));
      searchTerms = _map(searchTerms, (term: string) => term.toLowerCase());

      const filteredEntities: IACOption[] = this.formattedEntities.filter(entity => {
        return searchTerms.every(term => {
          return entity.human.toLowerCase().includes(term) || entity.key.toLowerCase().includes(term);
        });
      });
      this.menuHeight = this.calculateMenuHeight(filteredEntities.length);
      return filteredEntities;
    }

    this.menuHeight = this.calculateMenuHeight(this.entities.length);
    return this.formattedEntities;
  }

  public goToEntity (event: IACOption): void {
    this.selectedEntityId.emit(event.key);
    this.virtualScroll?.scrollToIndex(0);
  }

  public clearSearch (): void {
    this.entitySearchControl.setValue('');
  }
}
